import {
    MouseEvent as ReactMouseEvent,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import ChevronIcon from "./icons/ChevronIcon";
import {
    SizeVariants,
    tailwindSizeVariants,
    tailwindStyleVariants,
    VariantStyles,
} from "../types/styles";
import XMarkIcon from "./icons/XMarkIcon";

interface DropdownProps {
    id?: string;
    placeholder: string;
    dropdownItems: DropdownItem[];
    onChange?: (value: DropdownItem) => void;
    onCancel?: () => void;
    renderDropdownItem?: (item: DropdownItem) => ReactNode;
    variant?: keyof VariantStyles;
    size?: keyof SizeVariants;
    value?: string;
    disabled?: boolean;
}

type DropdownItem = {
    label: string;
    value?: string;
};

function Dropdown({
    id,
    placeholder,
    dropdownItems,
    onChange,
    onCancel,
    renderDropdownItem = (dropdownItem) => dropdownItem.label,
    variant = "primary",
    size = "lg",
    value,
    disabled = false,
}: DropdownProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [internalItem, setInternalItem] = useState<DropdownItem | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fallback chain: parent > self > placeholder
    const controlledItem = value
        ? dropdownItems.find((item) => item.value === value)
        : undefined;
    const selectedItem = controlledItem ??
        internalItem ?? { label: placeholder };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // TODO: Create hook for click outside
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    function toggleDropdown(e: ReactMouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        setIsOpen((prevState) => !prevState);
    }

    function handleSelect(
        e: ReactMouseEvent<HTMLButtonElement>,
        item: DropdownItem
    ) {
        e.stopPropagation();
        setInternalItem(item);
        if (onChange) {
            onChange(item);
        }
        setIsOpen(false);
    }

    function clearSelection(e: ReactMouseEvent<SVGSVGElement>) {
        e.stopPropagation();
        if (onCancel) {
            onCancel();
        }
        setInternalItem(null);
    }

    return (
        <div
            id={id ? `${id}-dropdown` : "dropdown"}
            className="relative space-y-1"
            ref={dropdownRef}
        >
            <button
                className={`tw-rounded-sm tw-border flex h-12 items-center justify-between gap-2 px-3 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 ${tailwindStyleVariants.dropdown.base[variant]} ${tailwindSizeVariants[size]}`}
                onClick={toggleDropdown}
                disabled={disabled}
            >
                <span className="truncate">{selectedItem.label}</span>
                <div className="flex shrink-0 items-center gap-3">
                    {onCancel &&
                        !disabled &&
                        selectedItem.label !== placeholder && (
                            <XMarkIcon size="14" onClick={clearSelection} />
                        )}
                    {isOpen ? <ChevronIcon direction="up" /> : <ChevronIcon />}
                </div>
            </button>
            {isOpen && !disabled && (
                <ul
                    className={`text-md tw-rounded-sm absolute z-10 max-h-60 list-none overflow-auto border bg-white ${tailwindStyleVariants.dropdown.menu[variant]} ${tailwindSizeVariants[size]}`}
                >
                    {dropdownItems.map((item, index) => {
                        return (
                            <li key={index}>
                                <button
                                    id={`${id}-item-${index}`}
                                    className={`w-full border-b px-3 py-2 text-left ${tailwindStyleVariants.dropdown.hover[variant]}`}
                                    onClick={(e) => handleSelect(e, item)}
                                >
                                    {renderDropdownItem(item)}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default Dropdown;
